import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faUndo, faCheck, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import CautionFormModal from './CautionFormModal';
import ConfirmModal from './ConfirmModal';

const STATUS_STYLE = {
  active:    { label: 'Active', bg: '#dbeafe', color: '#1e40af' },
  recovered: { label: 'Récupérée', bg: '#dcfce7', color: '#166534' },
  mainlevee: { label: 'Mainlevée', bg: '#f3f4f6', color: '#6b7280' },
};

const TYPE_STYLE = {
  provisoire: { label: 'Provisoire', bg: '#fef9c3', color: '#a16207' },
  definitive: { label: 'Définitive', bg: '#fee2e2', color: '#991b1b' },
};

const CautionsTab = ({ projectId, readOnly }) => {
  const [cautions, setCautions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState({ open: false, caution: null });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'warning' });
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });

  const fetchCautions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/projects/${projectId}/cautions`);
      setCautions(res.data.data || []);
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchCautions(); }, [fetchCautions]);

  const handleCreate = async (form) => {
    setSaving(true);
    try { await api.post(`/projects/${projectId}/cautions`, form); setModal({ open: false, caution: null }); fetchCautions(); }
    catch (err) { setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur lors de la création' }); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    try { await api.put(`/projects/${projectId}/cautions/${modal.caution.id}`, form); setModal({ open: false, caution: null }); fetchCautions(); }
    catch (err) { setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur lors de la mise à jour' }); }
    finally { setSaving(false); }
  };

  const promptAction = (id, action) => {
    const isRecover = action === 'recover';
    setConfirmModal({
      open: true,
      title: isRecover ? 'Récupérer cette caution ?' : 'Confirmer la mainlevée ?',
      message: isRecover
        ? 'La caution sera marquée comme récupérée avec la date d\'aujourd\'hui.'
        : 'La caution sera marquée en mainlevée.',
      variant: isRecover ? 'success' : 'warning',
      onConfirm: async () => {
        try {
          await api.post(`/projects/${projectId}/cautions/${id}/${action}`);
          setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
          fetchCautions();
        } catch (err) {
          setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
          setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur' });
        }
      },
    });
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" /></div>;

  const totalActive = cautions.filter(c => c.status === 'active').reduce((s, c) => s + parseFloat(c.amount), 0);

  return (
    <div className="cautions-tab">
      {/* Summary + add */}
      <div className="cautions-header-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div>
            <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Total actif</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: totalActive > 0 ? '#991b1b' : '#10b981' }}>
              {totalActive.toLocaleString('fr-FR')} MAD
            </div>
          </div>
          <div style={{ width: 1, height: 30, background: '#e5e7eb' }} />
          <div>
            <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Cautions</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>{cautions.length}</div>
          </div>
        </div>
        {!readOnly && (
          <button
            onClick={() => setModal({ open: true, caution: null })}
            style={{ fontSize: '0.78rem', fontWeight: 600, padding: '7px 14px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer' }}
          >
            <FontAwesomeIcon icon={faPlus} size="sm" className="me-1" />Nouvelle caution
          </button>
        )}
      </div>

      {/* List */}
      {cautions.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '50px 20px', textAlign: 'center' }}>
          <FontAwesomeIcon icon={faShieldAlt} size="2x" style={{ color: '#e5e7eb', marginBottom: 10 }} />
          <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>Aucune caution enregistrée.</div>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          {cautions.map((c, i) => {
            const type = TYPE_STYLE[c.type] || TYPE_STYLE.provisoire;
            const status = STATUS_STYLE[c.status] || STATUS_STYLE.active;
            const isActive = c.status === 'active';

            return (
              <div key={c.id} className="cautions-row" style={{
                borderBottom: i < cautions.length - 1 ? '1px solid #f3f4f6' : 'none',
              }}>
                {/* Type badge */}
                <div style={{ flexShrink: 0 }}>
                  <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: type.bg, color: type.color }}>
                    {type.label}
                  </span>
                </div>

                {/* Amount + dates */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#111827' }}>
                    {Number(c.amount).toLocaleString('fr-FR')} MAD
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 1 }}>
                    Déposée le {new Date(c.date_posted).toLocaleDateString('fr-FR')}
                    {c.date_echeance && ` · Échéance: ${new Date(c.date_echeance).toLocaleDateString('fr-FR')}`}
                    {c.date_recovered && ` · Récupérée le ${new Date(c.date_recovered).toLocaleDateString('fr-FR')}`}
                    {c.bank_reference && ` · Réf: ${c.bank_reference}`}
                  </div>
                </div>

                {/* Status */}
                <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '2px 10px', borderRadius: 8, background: status.bg, color: status.color, flexShrink: 0 }}>
                  {status.label}
                </span>

                {/* Actions */}
                {isActive && !readOnly && (
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button onClick={() => setModal({ open: true, caution: c })} title="Modifier"
                      style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faEdit} size="xs" />
                    </button>
                    {c.type === 'provisoire' && (
                      <button onClick={() => promptAction(c.id, 'mainlevee')} title="Mainlevée"
                        style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FontAwesomeIcon icon={faUndo} size="xs" />
                      </button>
                    )}
                    <button onClick={() => promptAction(c.id, 'recover')} title="Récupérer"
                      style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #10b981', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faCheck} size="xs" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CautionFormModal isOpen={modal.open} onClose={() => setModal({ open: false, caution: null })} onSubmit={modal.caution ? handleUpdate : handleCreate} caution={modal.caution} loading={saving} />

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmLabel="Confirmer"
      />

      <ConfirmModal
        isOpen={errorModal.open}
        onClose={() => setErrorModal({ open: false, message: '' })}
        title="Erreur"
        message={errorModal.message}
        variant="danger"
      />
      <style>{`
        .cautions-header-bar {
          display: flex; align-items: center; justify-content: space-between;
          background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px 20px; margin-bottom: 16px;
          flex-wrap: wrap; gap: 12px;
        }
        .cautions-row {
          display: flex; align-items: center; gap: 14px; padding: 14px 20px;
        }
        @media (max-width: 768px) {
          .cautions-header-bar { flex-direction: column; align-items: stretch; gap: 10px; padding: 12px 14px; }
          .cautions-row { flex-wrap: wrap; gap: 8px; padding: 12px 14px; }
        }
      `}</style>
    </div>
  );
};

export default CautionsTab;
